import type { Schemas } from "@10twenty/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, errorMessage, unwrap } from "../api";
import { useAuth, useMe } from "../auth/AuthContext";
import { useChat } from "../chat/ChatContext";
import { pinnedLocation, pinnedMapLabel } from "../chat/location";
import { groupCircles } from "../circles/circles";
import type { HistoryPoint } from "../history/history";
import { chooseCircle, chosenCircle, forgetCircle } from "../circles/choice";
import { MemberSidebar } from "../components/MemberSidebar";
import { SosBanners } from "../components/SosBanners";
import { countdownText, eventStatus, localTimeZone, scheduleText } from "../events/events";
import { isStale, memberRows } from "../live/liveCircle";
import { useLiveCircle } from "../live/useLiveCircle";
import { useSosActions } from "../live/useSosActions";
import { groupKey, retarget, shownTracks } from "../live/togetherView";
import { trailPieces } from "../live/trail";
import { useMovingTrails } from "../live/useMovingTrails";
import { useTrail } from "../live/useTrail";
import { useTravelGroups } from "../live/useTravelGroups";
import { useNow } from "../useNow";

const LiveMap = lazy(() => import("../map/LiveMap"));

/** Re-evaluates staleness and "n min ago" labels this often. */
const TICK_MS = 30_000;
const NO_CIRCLE_POLL_MS = 30_000;

export function MapPage() {
  const { session } = useAuth();
  const me = useMe();
  const queryClient = useQueryClient();
  const userId = session?.userId ?? "";
  const circles = useQuery({
    queryKey: ["circles"],
    queryFn: async () => unwrap(await api.GET("/circles")),
    refetchInterval: (query) => (query.state.data?.length === 0 ? NO_CIRCLE_POLL_MS : false),
  });
  const [stored, setChosen] = useState(() => chosenCircle(userId));
  // `/?circle=<id>` (an SOS notification) opens that circle's map until another is chosen.
  const [params, setParams] = useSearchParams();
  const requested = params.get("circle");
  const chosen = requested ?? stored;
  useEffect(() => {
    if (requested && userId) chooseCircle(userId, requested);
  }, [requested, userId]);
  // A circle the stream reported lost stays out of the choice until the list is fetched again.
  const [lost, setLost] = useState<{ id: string; listAt: number } | null>(null);
  const lostId = lost?.listAt === circles.dataUpdatedAt ? lost.id : null;

  const nowMs = useNow(TICK_MS);
  // Family, then friends, then events (ended ones last), so the first is the natural default.
  const groups = useMemo(
    () =>
      groupCircles(
        (circles.data ?? []).filter((c) => c.id !== lostId),
        nowMs,
      ),
    [circles.data, lostId, nowMs],
  );
  const available = useMemo(() => groups.flatMap((group) => group.circles), [groups]);
  const circle = available.find((c) => c.id === chosen) ?? available[0];

  const choose = (id: string) => {
    chooseCircle(userId, id);
    setChosen(id);
    if (requested) setParams({}, { replace: true });
  };

  const onLost = useCallback(() => {
    if (!circle) return;
    forgetCircle(userId, circle.id);
    setLost({ id: circle.id, listAt: circles.dataUpdatedAt });
    void queryClient.invalidateQueries({ queryKey: ["circles"] });
  }, [circle, circles.dataUpdatedAt, queryClient, userId]);

  if (!session) return null;
  if (circles.isPending) return <p className="muted map-message">Loading your circles…</p>;
  if (circles.error) return <p className="error map-message">{errorMessage(circles.error)}</p>;
  if (!circle) {
    return (
      <div className="stack map-message">
        <h1>{me.data ? `Hi, ${me.data.name}` : "Welcome"}</h1>
        <p className="muted">
          You aren't in a circle yet. Open an invite link from a family member, or create a circle of your own.
        </p>
        <p>
          <Link to="/circles">Create or join a circle</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="map-page">
      {available.length > 1 && (
        <label className="map-circle-picker">
          <span className="visually-hidden">Circle</span>
          <select value={circle.id} onChange={(event) => choose(event.target.value)}>
            {groups.length === 1
              ? available.map((c) => <CircleOption key={c.id} circle={c} nowMs={nowMs} />)
              : groups.map((group) => (
                  <optgroup key={group.kind} label={group.title}>
                    {group.circles.map((c) => (
                      <CircleOption key={c.id} circle={c} nowMs={nowMs} />
                    ))}
                  </optgroup>
                ))}
          </select>
        </label>
      )}
      <CircleMap
        key={circle.id}
        circleId={circle.id}
        selfId={session.userId}
        deviceId={session.deviceId}
        event={circle.event ?? null}
        onLost={onLost}
      />
    </div>
  );
}

function CircleOption({ circle, nowMs }: { circle: Schemas["Circle"]; nowMs: number }) {
  const status = circle.event ? eventStatus(circle.event, nowMs) : null;
  return (
    <option value={circle.id}>
      {circle.name}
      {status === "live" ? " · Live" : status === "ended" ? " · Ended" : ""}
    </option>
  );
}

function CircleMap(props: {
  circleId: string;
  selfId: string;
  deviceId: string;
  event: Schemas["CircleEvent"] | null;
  onLost: () => void;
}) {
  const { circle, status, membersLoaded } = useLiveCircle(props.circleId, props.selfId, props.deviceId, props.onLost);
  const nowMs = useNow(TICK_MS);
  const rows = useMemo(() => memberRows(circle, props.selfId), [circle, props.selfId]);
  const located = rows.filter((row) => row.location || row.bubble);
  const locationOff = rows.filter((row) => row.locationOff).length;
  const stale = located.filter((row) => row.location && !row.locationOff && isStale(row, nowMs)).length;
  // A member's user id, or a group's `groupKey`.
  const [focus, setFocus] = useState<{ id: string; seq: number } | null>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const select = useCallback((id: string) => setFocus((prev) => ({ id, seq: (prev?.seq ?? 0) + 1 })), []);
  const showAll = useCallback(() => setFocus(null), []);
  // In the list, the selected row unselects it.
  const toggle = useCallback(
    (id: string) => setFocus((prev) => (prev?.id === id ? null : { id, seq: (prev?.seq ?? 0) + 1 })),
    [],
  );
  const sos = useSosActions(props.circleId);
  const event = props.event;
  const [viewerZone] = useState(localTimeZone);
  const focusRow = rows.find((row) => row.userId === focus?.id) ?? null;
  const focusTrail = useTrail(props.circleId, focusRow, nowMs);
  const moving = useMovingTrails(props.circleId, rows, nowMs, focusRow?.userId ?? null);
  const groups = useTravelGroups(rows, moving.tracks, nowMs, moving.grouping);
  // A followed group that changes its id or dissolves is followed on (`retarget`), without zooming again.
  const [seenGroups, setSeenGroups] = useState(groups);
  if (seenGroups !== groups) {
    setSeenGroups(groups);
    const id = focus && retarget(focus.id, seenGroups, groups);
    if (focus && id !== focus.id) setFocus({ id: id!, seq: focus.seq });
  }
  const focusGroup = groups.find((group) => groupKey(group.id) === focus?.id);
  const trail = useMemo(() => {
    // One trail per group (its freshest member's); a followed group's is drawn even with moving trails off.
    const drawn = moving.shown
      ? shownTracks(moving.tracks, groups, focusRow?.userId ?? null)
      : new Map<string, readonly HistoryPoint[]>();
    const groupTrack = focusGroup && moving.tracks.get(focusGroup.freshestId);
    if (groupTrack) drawn.set(focusGroup.freshestId, groupTrack);
    const near = new Map(rows.map((row) => [row.userId, row.location?.lon]));
    return [...[...drawn].flatMap(([id, points]) => trailPieces(points, near.get(id))), ...focusTrail];
  }, [moving, groups, focusRow?.userId, focusGroup, rows, focusTrail]);
  // The location pinned in this circle's chat (a meeting point, say).
  const { chat } = useChat();
  const chatConversation = chat?.conversations.find((c) => c.circle_id === props.circleId);
  const pinnedHere = chatConversation ? pinnedLocation(chatConversation, nowMs) : null;
  const chatPin = pinnedHere && {
    lat: pinnedHere.location.lat,
    lon: pinnedHere.location.lon,
    label: pinnedMapLabel(pinnedHere.location),
  };

  return (
    <>
      <Suspense fallback={<div className="live-map" />}>
        <LiveMap
          rows={rows}
          nowMs={nowMs}
          fitKey={props.circleId}
          groups={groups}
          onSelect={select}
          focus={focus}
          onShowAll={showAll}
          overlayRef={sidebarRef}
          trail={trail}
          meetingPoint={event && eventStatus(event, nowMs) !== "ended" ? event.meeting_point : null}
          pinned={chatPin}
        />
      </Suspense>
      <MemberSidebar
        ref={sidebarRef}
        circleId={props.circleId}
        rows={rows}
        groups={groups}
        nowMs={nowMs}
        selectedId={focus?.id ?? null}
        onSelect={toggle}
        sos={sos}
      />
      <SosBanners rows={rows} nowMs={nowMs} actions={sos} onSelect={select} />
      <div className="map-status" role="status">
        {event && eventStatus(event, nowMs) !== "live" ? (
          <span>
            {eventStatus(event, nowMs) === "upcoming"
              ? `${countdownText(event, nowMs)} (${scheduleText(event, viewerZone)}). Locations show only during the event.`
              : "This event has ended. Locations are no longer shared."}
          </span>
        ) : status === "reconnecting" ? (
          <span className="map-status-warning">Reconnecting… locations may be out of date.</span>
        ) : status === "connecting" ? (
          <span>Connecting…</span>
        ) : status === "stopped" ? null : membersLoaded ? (
          <span>
            {located.length} of {rows.length} on the map
            {stale > 0 && ` · ${stale} not updated for 30 min`}
            {locationOff > 0 && ` · ${locationOff} with Location off`}
          </span>
        ) : null}
      </div>
    </>
  );
}
